import React, { useRef, useState } from 'react';

import { defaultIconSize, defaultIconSpacing } from '../../../utils/helper';
import { getIconByName } from '../../../../../reusable/IconLibrary/IconLibrary';

import { getBorderStyle, getDropShadowStyle, getWidgetOpacity } from './helper';
import { WidgetProps } from './WidgetProps';
import { useWidgetAnimation } from './useWidgetAnimation';

const generateShapeContentStyle = ({
    isShowWidget,
    isExiting,
    style,
}: {
    isShowWidget?: boolean;
    isExiting?: boolean;
    style: WidgetProps.StyleProps;
}) => {
    const {
        transformSettings: { paddingBottom, paddingLeft, paddingRight, paddingTop },
    } = style;

    // Grid stacking (NOT position:absolute): a CSS `position` on HTML content inside
    // a foreignObject mis-paints on Safari (correct getBoundingClientRect, wrong
    // paint). A single-cell grid overlays the shape layer and the icon with pure
    // layout - no compositing layer - so it paints correctly on Safari.
    return {
        display: isShowWidget || isExiting ? 'grid' : 'none',
        gridTemplateColumns: '1fr',
        gridTemplateRows: '1fr',
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        width: '100%',
        height: '100%',
    } as React.CSSProperties;
};

const generateShapeWrapperVisualStyle = ({
    shapeType,
    style,
    isHover,
    isClickable,
}: {
    shapeType?: string;
    style: WidgetProps.StyleProps;
    isHover?: boolean;
    isClickable?: boolean;
}) => {
    const {
        transformSettings: {
            borderRadiusBottom,
            borderRadiusLeft,
            borderRadiusRight,
            borderRadiusTop,
        },
        backgroundSettings: {
            backgroundColor,
            hoverBackgroundColor,
        },
    } = style;

    const { border, hoverBorder } = getBorderStyle(style.backgroundSettings);

    const baseStyle = {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        width: '100%',
        height: '100%',
        backgroundColor: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
        border: isHover ? (hoverBorder || border) : border,
    } as React.CSSProperties;

    switch (shapeType) {
        case 'circle':
            return { ...baseStyle, borderRadius: '50%' };
        case 'triangle':
            return { ...baseStyle, clipPath: 'polygon(50% 0%, 0% 100%, 100% 100%)' };
        case 'rectangle':
        default:
            return {
                ...baseStyle,
                borderTopRightRadius: `${borderRadiusTop}px`,
                borderBottomRightRadius: `${borderRadiusRight}px`,
                borderBottomLeftRadius: `${borderRadiusBottom}px`,
                borderTopLeftRadius: `${borderRadiusLeft}px`,
            };
    }
};

// Triangle is rendered as a real SVG <polygon>, NOT a clip-path div: a CSS `clip-path`
// on HTML content inside a foreignObject promotes it to a compositing layer that Safari
// positions in the wrong coordinate space (same family as transform/opacity/position -
// only triangle broke; circle uses border-radius, square uses border-radii). The polygon
// paints in place. fill/stroke are set on the <svg> and inherited by the polygon, so the
// press colour-flash (which writes `fill` on an <svg> bgElementRef) still works.
const generateTriangleStyle = ({
    style,
    isHover,
}: {
    style: WidgetProps.StyleProps;
    isHover?: boolean;
}) => {
    const {
        backgroundSettings: { backgroundColor, hoverBackgroundColor, borderColor, hoverBorderColor, borderWidth, hoverBorderWidth },
    } = style;
    const strokeWidth = isHover ? (hoverBorderWidth ?? borderWidth) : borderWidth;
    return {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        width: '100%',
        height: '100%',
        fill: isHover ? (hoverBackgroundColor || backgroundColor) : backgroundColor,
        stroke: isHover ? (hoverBorderColor || borderColor) : borderColor,
        strokeWidth: strokeWidth || 0,
    } as React.CSSProperties;
};

const generateIconStyle = ({
    isHover,
    size,
    color,
    hoverColor,
    isClickable,
}: {
    isHover: boolean;
    size: string;
    color: string;
    hoverColor: string;
    isClickable?: boolean;
}) => {
    return {
        fontSize: `${size}px`,
        color: isHover ? (hoverColor || color) : color,
    };
};

const generateWrapperStyle = ({
    style,
    isHover,
    isClickable,
}: {
    style: WidgetProps.StyleProps;
    isHover: boolean;
    isClickable?: boolean;
}) => {
    const { dropShadow, hoverDropShadow } = getDropShadowStyle(style.transformSettings);
    return {
        filter: "drop-shadow(" + (isHover ? (hoverDropShadow || dropShadow) : dropShadow) + ")",
    } as React.CSSProperties;
};

const generateIconContainerStyle = ({ spacing }: { spacing: string }) => {
    return {
        gridColumn: '1 / 1',
        gridRow: '1 / 1',
        placeSelf: 'center',
        paddingBottom: `${spacing}px`,
        paddingLeft: `${spacing}px`,
        paddingRight: `${spacing}px`,
        paddingTop: `${spacing}px`,
        lineHeight: 0,
    } as React.CSSProperties;
};

const ShapesWidget = ({
    id,
    isShowWidget,
    isExiting,
    onExitDone,
    itemRef,
    onClickItem,
    widgetTemplateId,
    style,
    isSafari,
    isClickable,
    settings,
    onContextMenu,
}: WidgetProps.BaseProps) => {
    const {
        textLabel: { color, hoverColor },
        iconConfig: { size = defaultIconSize, spacing = defaultIconSpacing } = {},
    } = style;

    const [isHover, setIsHover] = useState(false);
    const gElementRef = useRef<SVGGElement | null>();
    const iconElementRef = useRef<HTMLDivElement | null>();
    const bgElementRef = useRef<HTMLDivElement | SVGSVGElement | null>();

    const handleMouseEnter = () => {
        setIsHover(true);
    };

    const handleMouseLeave = () => {
        setIsHover(false);
    };

    const { iconKey, shapeType } = settings;
    const icon =
        iconKey &&
        getIconByName(iconKey, {
            style: generateIconStyle({ isHover, size, color, hoverColor: hoverColor || color, isClickable }),
        });

    const { triggerClickAnimation } = useWidgetAnimation({ gElementRef, iconElementRef, bgElementRef, style, isShowWidget, isExiting, onExitDone, isClickable });

    return (
        <g id={widgetTemplateId} className={`${!isSafari ? 'widget' : ''} widget_${widgetTemplateId}`} style={{ opacity: getWidgetOpacity(style.backgroundSettings, isHover) }}>
            <g ref={itemRef}>
                <g
                    ref={r => gElementRef.current = r}
                    data-template-id={widgetTemplateId}
                    data-widget-id={id}
                    onClick={(e) => {
                        e.stopPropagation();
                        if (!isShowWidget) { return; }
                        triggerClickAnimation(() => onClickItem?.(itemRef));
                    }}
                    onContextMenu={(e) => {
                        e.preventDefault();
                        onContextMenu?.(itemRef, !!isShowWidget);
                        return false;
                    }}
                    style={generateWrapperStyle({ style, isHover, isClickable })}
                >
                    <foreignObject>
                        <div
                            className="shapes-widget"
                            style={generateShapeContentStyle({ isShowWidget, isExiting, style })}
                            onMouseEnter={handleMouseEnter}
                            onMouseLeave={handleMouseLeave}
                        >
                            {shapeType === 'triangle' ? (
                                <svg
                                    ref={r => bgElementRef.current = r}
                                    className="shapes-widget__shape shapes-widget__shape--triangle"
                                    width="100%"
                                    height="100%"
                                    viewBox="0 0 100 100"
                                    preserveAspectRatio="none"
                                    style={generateTriangleStyle({ style, isHover })}
                                >
                                    <polygon points="50,0 0,100 100,100" vectorEffect="non-scaling-stroke" />
                                </svg>
                            ) : (
                                <div
                                    ref={r => bgElementRef.current = r}
                                    className={`shapes-widget__shape shapes-widget__shape--${shapeType}`}
                                    style={generateShapeWrapperVisualStyle({ shapeType, style, isHover, isClickable })}
                                />
                            )}
                            {iconKey && (
                                <div ref={r => iconElementRef.current = r} style={generateIconContainerStyle({ spacing })}>{icon}</div>
                            )}
                        </div>
                    </foreignObject>
                </g>
            </g>
        </g>
    );
};

export default ShapesWidget;
